import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, Truck, CreditCard, MapPin } from 'lucide-react';
import { createOrder, PAYMENT_METHOD_LABELS, DELIVERY_METHOD_LABELS } from '@/services/ordersApi';
import { toast } from '@/hooks/use-toast';
import { DeliveryAddressSelection } from '@/components/checkout/DeliveryAddressSelection';

const TVA_RATE = 0.20; // 20% TVA

const Checkout = () => {
  const navigate = useNavigate();
  const { items, clearCart, getTotalPrice } = useCart();
  const { partner, token } = useAuth();
  
  const [paymentMethod, setPaymentMethod] = useState<'cash_on_delivery' | 'transfer' | 'naps'>('transfer');
  const [deliveryMethod, setDeliveryMethod] = useState<'free_delivery' | 'store'>('store');
  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState<number | null>(null);
  const [additionalMessage, setAdditionalMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (items.length === 0) {
      navigate('/');
      return;
    }

    if (!partner) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour passer commande",
        variant: "destructive",
      });
      navigate('/');
      return;
    }
  }, [items, partner, navigate]);

  const subtotalHT = getTotalPrice();
  const tvaAmount = subtotalHT * TVA_RATE;
  const totalTTC = subtotalHT + tvaAmount;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(price);
  };

  const handleSubmitOrder = async () => {
    if (!token || items.length === 0) return;

    try {
      setLoading(true);

      const orderLines = items.map(item => ({
        product_id: parseInt(item.id),
        qty: item.quantity,
      }));

      const orderData = {
        lines: orderLines,
        payment_method: paymentMethod,
        delivery_method: deliveryMethod,
        delivery_address_id: selectedDeliveryAddressId || undefined,
        additional_message: additionalMessage || undefined,
      };

      const response = await createOrder(token, orderData);

      // Clear cart and redirect to confirmation
      clearCart();
      navigate(`/order-confirmation/${response.order_id}`, {
        state: { orderData: response }
      });

      toast({
        title: "Commande créée",
        description: `Votre commande ${response.name} a été créée avec succès`,
      });

    } catch (error) {
      console.error('Error creating order:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer votre commande. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Finaliser ma commande</h1>

        <div className="grid gap-8 lg:grid-cols-2">
          {/* Left Column - Order Details */}
          <div className="space-y-6">
            {/* Order Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Résumé de commande
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="flex justify-between items-center">
                    <div className="flex-1">
                      <h4 className="font-medium">{item.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        Quantité: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatPrice(item.price * item.quantity)}</p>
                      <p className="text-sm text-muted-foreground">HT</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Payment Method */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Mode de paiement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={paymentMethod} onValueChange={(value) => setPaymentMethod(value as any)}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="transfer" id="transfer" />
                    <Label htmlFor="transfer">{PAYMENT_METHOD_LABELS.transfer}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="cash_on_delivery" id="cash_on_delivery" />
                    <Label htmlFor="cash_on_delivery">{PAYMENT_METHOD_LABELS.cash_on_delivery}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="naps" id="naps" />
                    <Label htmlFor="naps">{PAYMENT_METHOD_LABELS.naps}</Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Delivery Method */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  Mode de livraison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={deliveryMethod} onValueChange={(value) => setDeliveryMethod(value as any)}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="store" id="store" />
                    <Label htmlFor="store">{DELIVERY_METHOD_LABELS.store}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="free_delivery" id="free_delivery" />
                    <Label htmlFor="free_delivery">{DELIVERY_METHOD_LABELS.free_delivery}</Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Delivery Address Selection - Only shown for free delivery */}
            <DeliveryAddressSelection
              selectedAddressId={selectedDeliveryAddressId}
              onAddressSelected={setSelectedDeliveryAddressId}
              deliveryMethod={deliveryMethod}
            />

            {/* Additional Message */}
            <Card>
              <CardHeader>
                <CardTitle>Message additionnel (optionnel)</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Ajoutez des instructions spéciales pour votre commande..."
                  value={additionalMessage}
                  onChange={(e) => setAdditionalMessage(e.target.value)}
                  rows={3}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Order Total & Confirmation */}
          <div className="space-y-6">
            {/* Customer Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Informations client
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="font-medium">{partner?.name}</p>
                  <p className="text-sm text-muted-foreground">{partner?.email}</p>
                </div>
              </CardContent>
            </Card>

            {/* Price Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Récapitulatif des prix</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span>Sous-total HT</span>
                  <span>{formatPrice(subtotalHT)}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="flex items-center gap-2">
                    TVA (20%)
                    <Badge variant="secondary" className="text-xs">
                      {(TVA_RATE * 100).toFixed(0)}%
                    </Badge>
                  </span>
                  <span>{formatPrice(tvaAmount)}</span>
                </div>
                
                <Separator />
                
                <div className="flex justify-between text-lg font-bold">
                  <span>Total TTC</span>
                  <span>{formatPrice(totalTTC)}</span>
                </div>

                <Button 
                  onClick={handleSubmitOrder} 
                  className="w-full mt-6" 
                  size="lg"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Création en cours...
                    </div>
                  ) : (
                    'Confirmer la commande'
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  En confirmant votre commande, vous acceptez nos conditions générales de vente.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;